// Ordenação da lista de usuários pelo clique no cabeçalho da coluna.
//
// A ordenação é feita aqui, no que já veio da API — a lista de usuários do
// admin é pequena (dezenas) e cabe inteira na tela. Se um dia passar de umas
// centenas, isto vira parâmetro de query e o servidor ordena.

import type { User } from '@/types/user.types';

export type ColunaOrdenavel = 'nome' | 'assinatura' | 'propriedade' | 'ultimoAcesso';
export type Direcao = 'asc' | 'desc';

export interface Ordenacao {
  coluna: ColunaOrdenavel;
  direcao: Direcao;
}

/**
 * Peso de cada status de assinatura.
 *
 * Ordem alfabética não serviria: o que interessa a quem administra é ver
 * primeiro quem está prestes a sair, e não a letra inicial da palavra. Maior
 * peso = mais perto de virar problema.
 *
 * `comp` (cortesia) fica acima de `active` porque é acesso liberado sem
 * receita — vale ficar de olho, mesmo não sendo urgente. As chaves têm que
 * bater com ASSINATURA_LABEL; status fora desta lista cai em 0 e some no meio
 * de quem não tem assinatura, que foi exatamente o que aconteceu com a
 * cortesia na primeira versão disto.
 */
export const PESO_ASSINATURA: Record<string, number> = {
  past_due: 6,
  canceled: 5,
  expired: 4,
  trial: 3,
  comp: 2,
  active: 1,
  none: 0,
};

const pesoAssinatura = (u: User): number => {
  const status = u.assinatura?.status;
  if (!status) return 0; // sem assinatura (funcionário, por exemplo)
  return PESO_ASSINATURA[status] ?? 0;
};

const nomeDaPropriedade = (u: User): string => {
  if (!u.property) return '';
  return typeof u.property === 'string' ? u.property : u.property.name || '';
};

// Nunca acessou vira 0: com `asc` aparece primeiro, com `desc` por último —
// que é o que se espera de "quem está mais parado" / "quem entrou agora".
const instanteDoAcesso = (u: User): number => {
  const t = new Date(u.lastAccess ?? '').getTime();
  return Number.isNaN(t) ? 0 : t;
};

// `localeCompare` com pt-BR para acento não jogar "Ávila" pro fim da lista, e
// `sensitivity: base` para maiúscula não separar "salatiel" de "Salatiel" —
// os dois aparecem assim no cadastro real.
const compararTexto = (a: string, b: string): number =>
  a.localeCompare(b, 'pt-BR', { sensitivity: 'base' });

/**
 * Compara pelo critério da coluna. Devolve 0 quando empatam — quem chama
 * resolve o empate e a direção.
 */
function compararPrimario(a: User, b: User, coluna: ColunaOrdenavel): number {
  switch (coluna) {
    case 'nome':
      return compararTexto(a.name || '', b.name || '');
    case 'propriedade':
      return compararTexto(nomeDaPropriedade(a), nomeDaPropriedade(b));
    case 'assinatura':
      return pesoAssinatura(a) - pesoAssinatura(b);
    case 'ultimoAcesso':
      return instanteDoAcesso(a) - instanteDoAcesso(b);
  }
}

/** Devolve uma cópia ordenada — não mexe no array que veio do React Query. */
export function ordenarUsuarios(users: User[], ordenacao: Ordenacao | null): User[] {
  if (!ordenacao) return users;

  const { coluna, direcao } = ordenacao;
  const fator = direcao === 'asc' ? 1 : -1;

  return [...users].sort((a, b) => {
    // Quem não tem propriedade fica no fim NOS DOIS sentidos. Ausência de dado
    // não é um valor que compete na ordem — inverter junto só afastaria a
    // informação útil do topo.
    if (coluna === 'propriedade') {
      const pa = nomeDaPropriedade(a);
      const pb = nomeDaPropriedade(b);
      if (!pa && pb) return 1;
      if (pa && !pb) return -1;
    }

    const primario = compararPrimario(a, b, coluna);
    if (primario !== 0) return primario * fator;

    // Desempate SEMPRE em A-Z, sem aplicar o fator: ele existe para a ordem
    // ficar estável entre cliques. Se invertesse junto, uma lista de dez
    // usuários em trial se embaralharia a cada clique e pareceria bug.
    return compararTexto(a.name || '', b.name || '');
  });
}

/**
 * Próximo estado ao clicar num cabeçalho.
 *
 * Clicar numa coluna nova começa pela ordem mais útil dela: nome e propriedade
 * em A-Z; último acesso e assinatura do "mais recente/mais urgente" para baixo,
 * que é o que se quer ver primeiro. Clicar de novo inverte; no terceiro clique
 * a ordenação sai e a lista volta como veio da API.
 */
export function proximaOrdenacao(
  atual: Ordenacao | null,
  coluna: ColunaOrdenavel
): Ordenacao | null {
  const inicial: Direcao = coluna === 'nome' || coluna === 'propriedade' ? 'asc' : 'desc';

  if (!atual || atual.coluna !== coluna) return { coluna, direcao: inicial };
  if (atual.direcao === inicial) return { coluna, direcao: inicial === 'asc' ? 'desc' : 'asc' };
  return null;
}
